import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahgbpboh653ivfvnuag';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const topics = [
  '政客丑闻',
  '财阀内幕',
  '官员贪腐',
  '选举黑幕',
  '企业造假',
  '学术不端',
  '内容指正',
  '其他事项',
];

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [detail, setDetail] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('sending');
    setErrorMsg('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const successCode = parsed?.code === 'OK';

      if (!response.ok || !successCode || isSpam) {
        setStatus('error');
        setErrorMsg(
          serverMsg && serverMsg.length < 160
            ? serverMsg
            : '提交没有成功，请检查填写内容后重试，或直接发邮件到 editor@meirichigua.com。',
        );
        return;
      }

      form.reset();
      setDetail('');
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('网络连接出现问题，请稍后重试，或直接发邮件到 editor@meirichigua.com。');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 md:p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-xl text-accent-950">
          <i className="ri-check-line" />
        </span>
        <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">线索已收到</h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
          感谢你的投递。编辑会在两个工作日内通过你留下的邮箱联系核对细节。如果涉及敏感内容，我们不会在来信中直接提及具体信息。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 cursor-pointer whitespace-nowrap rounded-full border border-accent-400 px-5 py-2.5 font-label text-sm font-medium text-accent-800 transition-colors hover:bg-accent-100"
        >
          再提交一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      id="gossip-tip-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300/70 bg-background-100 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="field-name" className="mb-1.5 block font-label text-sm text-foreground-800">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="field-name"
            name="name"
            type="text"
            required
            placeholder="可以是化名"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="field-email" className="mb-1.5 block font-label text-sm text-foreground-800">
            联络邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="field-email"
            name="email"
            type="email"
            required
            placeholder="用于核对细节，不会公开"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="field-topic" className="mb-1.5 block font-label text-sm text-foreground-800">
            线索类型
          </label>
          <select
            id="field-topic"
            name="topic"
            defaultValue={topics[0]}
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            {topics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="field-organization"
            className="mb-1.5 block font-label text-sm text-foreground-800"
          >
            涉及机构或地区
          </label>
          <input
            id="field-organization"
            name="organization"
            type="text"
            placeholder="选填，例如某州议会 / 某商社"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="field-detail" className="mb-1.5 block font-label text-sm text-foreground-800">
          线索详情 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="field-detail"
          name="detail"
          required
          rows={6}
          maxLength={500}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          placeholder="请简要说明事件经过、涉及的时间点，以及你手上有哪些可核对的材料（如文件类型、可查编号等）。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <p className="mt-1.5 text-right font-label text-xs text-foreground-500">
          {detail.length} / 500
        </p>
      </div>

      <div className="gossip-field-verify" aria-hidden="true">
        <label htmlFor="field-website-alt">请勿填写此栏</label>
        <input
          id="field-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === 'error' ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm leading-relaxed text-primary-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {errorMsg}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 border-t border-background-300/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们为核查目的联系你。来源信息严格保密，未经同意不会公开。
        </p>
        <button
          type="submit"
          disabled={status === 'sending'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={status === 'sending' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
          </span>
          {status === 'sending' ? '正在提交' : '提交线索'}
        </button>
      </div>
    </form>
  );
}